// Simple encryption/decryption utility for URL parameters
// Note: This is a basic implementation. For production, consider using a more robust encryption library

const ENCRYPTION_KEY = 'default-key-change-in-production';

// Simple XOR encryption (for demonstration purposes)
// In production, use a proper encryption library like crypto-js
function simpleEncrypt(text: string, key: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(result); // Base64 encode
}

function simpleDecrypt(encryptedText: string, key: string): string {
  try {
    const decoded = atob(encryptedText); // Base64 decode
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  } catch (error) {
    throw new Error('Failed to decrypt data');
  }
}

export interface LiveLocationData {
  deviceId: number;
  latitude: number;
  longitude: number;
  timeFrame: number; // in minutes
  userToken: string;
  vehicleName?: string;
  // Epoch milliseconds when the link was generated
  issuedAt: number;
}

export function encryptLiveLocationData(data: LiveLocationData): string {
  const jsonString = JSON.stringify(data);
  return simpleEncrypt(jsonString, ENCRYPTION_KEY);
}

export function decryptLiveLocationData(encryptedData: string): LiveLocationData {
  const decryptedString = simpleDecrypt(encryptedData, ENCRYPTION_KEY);
  return JSON.parse(decryptedString);
}

export function generateLiveLocationUrl(data: LiveLocationData): string {
  const encrypted = encryptLiveLocationData(data);
  const baseUrl = window.location.origin;
  return `${baseUrl}/live-tracking/${encrypted}`;
}

// Helpers for expiry handling
export function getExpiryTimeMs(data: Pick<LiveLocationData, 'issuedAt' | 'timeFrame'>): number {
  return data.issuedAt + data.timeFrame * 60 * 1000;
}

export function isLiveLocationExpired(data: Pick<LiveLocationData, 'issuedAt' | 'timeFrame'>, now: number = Date.now()): boolean {
  return now > getExpiryTimeMs(data);
}

export function getRemainingMs(data: Pick<LiveLocationData, 'issuedAt' | 'timeFrame'>, now: number = Date.now()): number {
  return Math.max(0, getExpiryTimeMs(data) - now);
}
